<template>
  <div class="specification-manage-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('resourceStoreSpecificationManage.page.title')" :subtitle="$t('resourceStoreSpecificationManage.page.subtitle')">
      <template #extra>
        <el-button type="primary" size="small" icon="el-icon-plus" @click="openAddModal">
          {{ $t('resourceStoreSpecificationManage.add') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="conditions.parentRstId" clearable class="w-full"
            :placeholder="$t('resourceStoreSpecificationManage.selectItemType')" @change="handleParentTypeChange">
            <el-option v-for="item in resourceStoreTypes" :key="item.rstId" :label="item.name" :value="item.rstId" />
          </el-select>
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="conditions.rstId" clearable class="w-full"
            :placeholder="$t('resourceStoreSpecificationManage.selectSubCategory')">
            <el-option v-for="item in resourceStoreSonTypes" :key="item.rstId" :label="item.name" :value="item.rstId" />
          </el-select>
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model="conditions.specName" clearable class="w-full"
            :placeholder="$t('resourceStoreSpecificationManage.specNamePlaceholder')"
            @keyup.enter.native="queryResourceStoreSpecification" />
        </el-col>
        <el-col v-show="moreCondition" :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model="conditions.rssId" clearable class="w-full"
            :placeholder="$t('resourceStoreSpecificationManage.specIdPlaceholder')"
            @keyup.enter.native="queryResourceStoreSpecification" />
        </el-col>
      </el-row>
      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="queryResourceStoreSpecification">
          {{ $t('resourceStoreSpecificationManage.query') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="resetResourceStoreSpecification">
          {{ $t('resourceStoreSpecificationManage.reset') }}
        </el-button>
        <el-button type="text" @click="toggleMoreCondition">
          {{ moreCondition ? $t('resourceStoreSpecificationManage.hide') : $t('resourceStoreSpecificationManage.more') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density" :show-column-setting="false"
        :show-refresh="true" @update:density="density = $event" @refresh="getList">
        <template #left>
          <span class="ds-panel__title">{{ $t('resourceStoreSpecificationManage.itemSpecifications') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="resourceStoreSpecifications" :loading="loading" :error="hasError"
        :error-message="errorMessage" :density="density" border
        :empty-description="$t('resourceStoreSpecificationManage.page.empty')" @retry="getList">
        <el-table-column prop="rssId" :label="$t('resourceStoreSpecificationManage.specId')" align="center" width="140">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.rssId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('resourceStoreSpecificationManage.typeName')" align="center" min-width="180">
          <template slot-scope="scope">
            {{ scope.row.parentRstName }}{{ scope.row.rstName ? ' > ' + scope.row.rstName : '' }}
          </template>
        </el-table-column>
        <el-table-column prop="specName" :label="$t('resourceStoreSpecificationManage.specName')" align="center" min-width="140" />
        <el-table-column prop="description" :label="$t('resourceStoreSpecificationManage.description')" align="center" min-width="160" />
        <el-table-column :label="$t('resourceStoreSpecificationManage.operation')" align="center" width="180" fixed="right">
          <template slot-scope="scope">
            <el-button type="primary" size="mini" @click="openEditModal(scope.row)">
              {{ $t('resourceStoreSpecificationManage.edit') }}
            </el-button>
            <el-button type="danger" size="mini" @click="openDeleteModal(scope.row)">
              {{ $t('resourceStoreSpecificationManage.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :page="page.current" :size="page.size" :total="page.total" :page-sizes="[10, 20, 30, 50]"
        @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>

    <!-- 子组件 -->
    <add-resource-store-specification ref="addModal" @success="handleSuccess" />
    <edit-resource-store-specification ref="editModal" @success="handleSuccess" />
    <delete-resource-store-specification ref="deleteModal" @success="handleSuccess" />
  </div>
</template>

<script>
import { listResourceStoreSpecifications, listResourceStoreTypes } from '@/api/resource/resourceStoreSpecificationManageApi'
import AddResourceStoreSpecification from '@/components/resource/addResourceStoreSpecification'
import EditResourceStoreSpecification from '@/components/resource/editResourceStoreSpecification'
import DeleteResourceStoreSpecification from '@/components/resource/deleteResourceStoreSpecification'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'ResourceStoreSpecificationManageList',
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      moreCondition: false,
      conditions: {
        specName: '',
        parentRstId: '',
        rstId: '',
        rssId: ''
      },
      resourceStoreSpecifications: [],
      resourceStoreTypes: [],
      resourceStoreSonTypes: [],
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  components: {
    AddResourceStoreSpecification,
    EditResourceStoreSpecification,
    DeleteResourceStoreSpecification,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar
  },
  created() {
    this.getList()
    this.getResourceStoreTypes()
  },
  methods: {
    async getList() {
      this.loading = true
      this.hasError = false
      try {
        const params = {
          ...this.conditions,
          page: this.page.current,
          row: this.page.size
        }

        const res = await listResourceStoreSpecifications(params)
        this.resourceStoreSpecifications = res.data
        this.page.total = res.total
      } catch (error) {
        console.error('Failed to get specification list:', error)
        this.hasError = true
        this.errorMessage = this.$t('common.loadError')
        this.$message.error(this.$t('common.queryFailed'))
      } finally {
        this.loading = false
      }
    },

    async getResourceStoreTypes() {
      try {
        const params = {
          page: 1,
          row: 100,
          parentId: '0'
        }

        const res = await listResourceStoreTypes(params)
        this.resourceStoreTypes = res.data
      } catch (error) {
        console.error('Failed to get item types:', error)
      }
    },

    async getResourceStoreSonTypes(parentRstId) {
      try {
        const params = {
          page: 1,
          row: 100,
          rstId: parentRstId,
          flag: "0"
        }

        const res = await listResourceStoreTypes(params)
        this.resourceStoreSonTypes = res.data
      } catch (error) {
        console.error('Failed to get sub item types:', error)
      }
    },

    handleParentTypeChange(val) {
      this.conditions.rstId = ''
      if (!val) {
        this.resourceStoreSonTypes = []
        return
      }
      this.getResourceStoreSonTypes(val)
    },

    toggleMoreCondition() {
      this.moreCondition = !this.moreCondition
    },

    queryResourceStoreSpecification() {
      this.page.current = 1
      this.getList()
    },

    resetResourceStoreSpecification() {
      this.conditions = {
        specName: '',
        parentRstId: '',
        rstId: '',
        rssId: ''
      }
      this.resourceStoreSonTypes = []
      this.page.current = 1
      this.getList()
    },

    openAddModal() {
      this.$refs.addModal.open()
    },

    openEditModal(row) {
      this.$refs.editModal.open(row)
    },

    openDeleteModal(row) {
      this.$refs.deleteModal.open(row)
    },

    handleSuccess() {
      this.getList()
    },

    handleSizeChange(size) {
      this.page.size = size
      this.page.current = 1
      this.getList()
    },

    handleCurrentChange(current) {
      this.page.current = current
      this.getList()
    }
  }
}
</script>

<style lang="scss" scoped>
.specification-manage-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
  margin-bottom: var(--ds-spacing-lg);

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

.w-full {
  width: 100%;
}
</style>
